import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/pagination';
import { defaultFavicon, fetchIcon } from '../../utils/iconUtils';
import { getCountryFlag } from '../../utils/countryUtils';
import { useEffect, useState } from 'react';
import { capitalizeFirstLetter } from '../../utils/caseUtils';
import { AnalyticsData } from '../../pages/Analytics';
import useI18n from '../../hooks/useI18n';

interface Slide {
  label: string;
  value?: string | null;
  logo?: string | null;
  indicate?: { direction: 'up' | 'down'; percent: number };
  unit?: string;
}

interface SlidesSwiperProps {
  analyticsData: AnalyticsData;
}

const StatsSwiper: React.FC<SlidesSwiperProps> = ({
  analyticsData: {
    topCountry,
    bestPerformingPlatform,
    topCity,
    totalClicked,
    bestTimeToEngage,
  },
}) => {
  const { t, currentLanguage } = useI18n();
  const [flag, setFlag] = useState<string | null>(null);
  const regionNames = new Intl.DisplayNames([currentLanguage], {
    type: 'region',
  });

  useEffect(() => {
    const fetchFlag = () => {
      if (topCountry && topCountry !== 'Unknown') {
        const countryFlag = getCountryFlag(topCountry);
        setFlag(countryFlag);
      } else {
        setFlag(null);
      }
    };

    fetchFlag();
  }, [topCountry]);

  const slidesData: Slide[] = [
    {
      label: String(t('analytics.totalClicks')),
      value: totalClicked.count.toString(),
      indicate: {
        direction: totalClicked.changeInPercentage >= 0 ? 'up' : 'down',
        percent: totalClicked.changeInPercentage,
      },
    },
    {
      label: String(t('analytics.bestPerformingPlatform')),
      logo: bestPerformingPlatform.platformName
        ? fetchIcon(bestPerformingPlatform.platformName)
        : null,
      value: bestPerformingPlatform.platformName
        ? capitalizeFirstLetter(bestPerformingPlatform.platformName)
        : String(t('common.noData')),
      indicate: {
        direction:
          bestPerformingPlatform.changeInPercentage >= 0 ? 'up' : 'down',
        percent: bestPerformingPlatform.changeInPercentage,
      },
    },
    {
      label: String(t('analytics.topCountry')),
      value:
        topCountry && topCountry !== 'Unknown'
          ? regionNames.of(topCountry)
          : String(t('common.noData')),
      logo: flag,
    },
    {
      label: String(t('analytics.topCity')),
      value:
        topCity && topCity !== 'Unknown' ? topCity : String(t('common.noData')),
    },
    {
      label: String(t('analytics.bestTimeToEngage')),
      value: bestTimeToEngage,
      unit: 'GMT',
    },
  ];

  return (
    <Swiper
      slidesPerView={5}
      spaceBetween={12}
      freeMode={true}
      breakpoints={{
        200: { slidesPerView: 1 },
        320: { slidesPerView: 1.6 },
        500: { slidesPerView: 2.1 },
        690: { slidesPerView: 2.7 },
        1080: { slidesPerView: 3.8 },
        1300: { slidesPerView: 4.2 },
        1400: { slidesPerView: 5 },
      }}
      modules={[FreeMode, Pagination]}
      className="mySwiper cursor-pointer"
      style={{ maxWidth: '100%' }}
    >
      {slidesData.map((slide, index) => (
        <SwiperSlide
          key={index}
          className="border px-[24px] py-[18px] rounded-3xl bg-white max-h-[100px] h-[80px] max-w-full !cursor-default flex flex-col justify-between"
          style={{
            width:
              slide.label !== String(t('analytics.bestTimeToEngage'))
                ? '180px'
                : 'auto', // Fixed width for all except 'Best Time To Engage'
            minWidth:
              slide.label === String(t('analytics.bestTimeToEngage'))
                ? '215px'
                : '180px', // Minimum width for 'Best Time To Engage'
            whiteSpace: 'nowrap', // Prevent line breaking for the content
          }}
        >
          <span className="text-xs font-content text-[#9B919D]">
            {slide.label || '-'}
          </span>
          <div className="flex items-center gap-2">
            {slide.label === String(t('analytics.topCountry')) &&
              slide.logo && (
                <img
                  src={slide.logo}
                  alt={slide.label}
                  className="h-[24px] w-[24px] rounded-full"
                />
              )}
            {slide.label === String(t('analytics.bestPerformingPlatform')) &&
              slide.logo && (
                <img
                  src={slide.logo}
                  alt={slide.label}
                  className="h-[24px] w-[24px]"
                />
              )}
            {slide.value ? (
              <span className="text-[#292828] text-[24px] font-[500] whitespace-nowrap">
                {slide.value || '-'}
              </span>
            ) : (
              <span className="text-[#292828] text-[24px] font-[500] whitespace-nowrap">
                {String(t('common.noData'))}
              </span>
            )}

            {slide.unit && slide.value && (
              <span className="text-xs font-content text-[#9B919D]">
                {slide.unit || '-'}
              </span>
            )}
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

export default StatsSwiper;
